import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userApi, syncAllStoreHours, calendarApi } from '../services/api';
import type { DayOfWeek } from 'shared';

const DAYS: { value: DayOfWeek; label: string }[] = [
  { value: 'MON', label: 'Mon' },
  { value: 'TUE', label: 'Tue' },
  { value: 'WED', label: 'Wed' },
  { value: 'THU', label: 'Thu' },
  { value: 'FRI', label: 'Fri' },
  { value: 'SAT', label: 'Sat' },
  { value: 'SUN', label: 'Sun' },
];

export function Settings() {
  const queryClient = useQueryClient();
  const [connectingCalendar, setConnectingCalendar] = useState(false);
  const [calendarConnectError, setCalendarConnectError] = useState<string | null>(null);

  const { data: user, isLoading } = useQuery({
    queryKey: ['user'],
    queryFn: () => userApi.get(),
  });

  const [formData, setFormData] = useState({
    name: '',
    home_address: '',
    work_days: [] as DayOfWeek[],
    work_start_time: '09:00',
    work_end_time: '17:00',
    calendar_url: '',
  });

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        home_address: user.home_address || '',
        work_days: user.work_days || [],
        work_start_time: user.work_start_time || '09:00',
        work_end_time: user.work_end_time || '17:00',
        calendar_url: user.calendar_url || '',
      });
    }
  }, [user]);

  const { data: calendarStatus, refetch: refetchCalendar } = useQuery({
    queryKey: ['calendar-status'],
    queryFn: () => calendarApi.status(),
  });
  const calendarConnected = calendarStatus?.connected ?? false;
  const icsUrl = calendarStatus?.icsUrl ?? null;
  const webcalUrl = icsUrl ? icsUrl.replace(/^https?:\/\//, 'webcal://') : null;
  const [icsCopied, setIcsCopied] = useState(false);

  const disconnectCalendarMutation = useMutation({
    mutationFn: () => calendarApi.disconnect(),
    onSuccess: () => refetchCalendar(),
  });

  const generateIcsMutation = useMutation({
    mutationFn: () => calendarApi.generateIcsToken(),
    onSuccess: () => refetchCalendar(),
  });

  const revokeIcsMutation = useMutation({
    mutationFn: () => calendarApi.revokeIcsToken(),
    onSuccess: () => refetchCalendar(),
  });

  function copyIcsUrl() {
    if (!icsUrl) return;
    navigator.clipboard.writeText(icsUrl).then(() => {
      setIcsCopied(true);
      setTimeout(() => setIcsCopied(false), 2000);
    });
  }

  // Handle redirect back from Google OAuth
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const calParam = params.get('calendar');
    if (calParam === 'connected' || calParam === 'error') {
      refetchCalendar();
      // Clean up query string without a full reload
      const url = new URL(window.location.href);
      url.searchParams.delete('calendar');
      window.history.replaceState({}, '', url.toString());
    }
  }, [refetchCalendar]);

  const syncMutation = useMutation({
    mutationFn: syncAllStoreHours,
  });

  const mutation = useMutation({
    mutationFn: () => userApi.update({
      ...formData,
      calendar_url: formData.calendar_url.trim() || null,
      // Always save the browser's current timezone so the scheduler converts times correctly
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
  });

  const toggleDay = (day: DayOfWeek) => {
    setFormData((prev) => ({
      ...prev,
      work_days: prev.work_days.includes(day)
        ? prev.work_days.filter((d) => d !== day)
        : [...prev.work_days, day],
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate();
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4" />
          <div className="h-40 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Settings</h1>

      <form onSubmit={handleSubmit} className="space-y-6 bg-white p-6 rounded-xl shadow-sm">
        {/* Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            Your Name
          </label>
          <input
            type="text"
            id="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
          />
        </div>

        {/* Home Address */}
        <div>
          <label htmlFor="home_address" className="block text-sm font-medium text-gray-700 mb-1">
            Home Address
          </label>
          <input
            type="text"
            id="home_address"
            value={formData.home_address}
            onChange={(e) => setFormData({ ...formData, home_address: e.target.value })}
            placeholder="123 Main St, City, State ZIP"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
          />
        </div>

        {/* Work Days */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Work Days
          </label>
          <div className="grid grid-cols-7 gap-2">
            {DAYS.map((day) => {
              const isSelected = formData.work_days.includes(day.value);
              return (
                <button
                  key={day.value}
                  type="button"
                  onClick={() => toggleDay(day.value)}
                  className={`
                    py-3 rounded-lg font-medium text-sm transition-all
                    ${isSelected
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }
                  `}
                >
                  {day.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Work Hours */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="work_start_time" className="block text-sm font-medium text-gray-700 mb-1">
              Start Time
            </label>
            <input
              type="time"
              id="work_start_time"
              value={formData.work_start_time}
              onChange={(e) => setFormData({ ...formData, work_start_time: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
            />
          </div>
          <div>
            <label htmlFor="work_end_time" className="block text-sm font-medium text-gray-700 mb-1">
              End Time
            </label>
            <input
              type="time"
              id="work_end_time"
              value={formData.work_end_time}
              onChange={(e) => setFormData({ ...formData, work_end_time: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
            />
          </div>
        </div>

        {/* Calendar Integration */}
        <div className="border-t pt-4">
          <h3 className="text-sm font-semibold text-gray-800 mb-1">Calendar Integration</h3>
          <p className="text-xs text-gray-500 mb-4">
            Connect your calendar so the scheduler blocks personal events and automatically locks in planned store visits.
            Works with iPhone (iCloud synced to Google) and Android.
          </p>

          {/* Google OAuth connect/disconnect */}
          <div className={`flex items-center justify-between p-3 rounded-lg border ${calendarConnected ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${calendarConnected ? 'bg-green-500' : 'bg-gray-400'}`}>
                {calendarConnected ? '✓' : 'G'}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-800">Google Calendar</p>
                <p className={`text-xs ${calendarConnected ? 'text-green-600' : 'text-gray-400'}`}>
                  {calendarConnected ? 'Connected — all calendars synced automatically' : 'Not connected'}
                </p>
              </div>
            </div>
            {calendarConnected ? (
              <button
                type="button"
                onClick={() => disconnectCalendarMutation.mutate()}
                disabled={disconnectCalendarMutation.isPending}
                className="px-3 py-1.5 text-xs font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors"
              >
                {disconnectCalendarMutation.isPending ? 'Disconnecting...' : 'Disconnect'}
              </button>
            ) : (
              <button
                type="button"
                disabled={connectingCalendar}
                onClick={async () => {
                  setConnectingCalendar(true);
                  setCalendarConnectError(null);
                  try {
                    const url = await calendarApi.getAuthUrl();
                    window.location.href = url;
                  } catch (err) {
                    setCalendarConnectError(err instanceof Error ? err.message : 'Connection failed');
                    setConnectingCalendar(false);
                  }
                }}
                className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {connectingCalendar ? 'Connecting...' : 'Connect'}
              </button>
            )}
          </div>
          {calendarConnectError && (
            <p className="mt-2 text-xs text-red-600">{calendarConnectError}</p>
          )}

          {/* iOS Calendar subscription */}
          <div className="mt-4">
            <p className="text-xs font-medium text-gray-700 mb-2">Subscribe on iPhone (iOS Calendar)</p>
            {icsUrl ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    readOnly
                    value={icsUrl}
                    className="flex-1 px-3 py-2 text-xs border border-gray-200 rounded-lg bg-gray-50 text-gray-600 font-mono truncate"
                  />
                  <button
                    type="button"
                    onClick={copyIcsUrl}
                    className="px-3 py-2 text-xs font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap"
                  >
                    {icsCopied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  {webcalUrl && (
                    <a
                      href={webcalUrl}
                      className="px-3 py-2 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Open in iPhone Calendar
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={() => revokeIcsMutation.mutate()}
                    disabled={revokeIcsMutation.isPending}
                    className="px-3 py-2 text-xs font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors"
                  >
                    Revoke Link
                  </button>
                </div>
                <p className="text-xs text-gray-400">
                  Tap "Open in iPhone Calendar" on your phone. Your schedule auto-refreshes hourly.
                </p>
              </div>
            ) : (
              <div>
                <button
                  type="button"
                  onClick={() => generateIcsMutation.mutate()}
                  disabled={generateIcsMutation.isPending}
                  className="px-3 py-2 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {generateIcsMutation.isPending ? 'Generating...' : 'Generate Calendar Link'}
                </button>
                <p className="text-xs text-gray-400 mt-1">
                  Creates a private link you can subscribe to from your iPhone.
                </p>
              </div>
            )}
          </div>

          {/* ICS URL fallback */}
          <details className="mt-3">
            <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600 select-none">
              Or use a manual calendar URL (advanced)
            </summary>
            <div className="mt-2">
              <input
                type="url"
                id="calendar_url"
                value={formData.calendar_url}
                onChange={(e) => setFormData({ ...formData, calendar_url: e.target.value })}
                placeholder="https:// or webcal:// iCal link"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition text-sm"
              />
              <p className="text-xs text-gray-400 mt-1">
                iCloud: Calendar app → tap calendar → Share → Copy Link. Outlook: Share → Publish → ICS.
              </p>
            </div>
          </details>
        </div>

        {/* Submit */}
        <div className="flex items-center justify-between pt-4 border-t">
          <div>
            {mutation.isSuccess && (
              <span className="text-green-600 text-sm">Settings saved!</span>
            )}
            {mutation.isError && (
              <span className="text-red-600 text-sm">Failed to save. Please try again.</span>
            )}
          </div>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {mutation.isPending ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>

      {/* Sync Store Hours */}
      <div className="mt-8 p-4 bg-gray-50 rounded-xl">
        <h3 className="text-sm font-medium text-gray-700 mb-2">Store Opening Hours</h3>
        <p className="text-sm text-gray-500 mb-3">
          Sync opening hours from Google Maps for all your stores. This helps the scheduler avoid booking visits before a store opens.
        </p>
        <button
          onClick={() => syncMutation.mutate()}
          disabled={syncMutation.isPending}
          className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium transition-colors"
        >
          {syncMutation.isPending ? 'Syncing...' : 'Sync Store Hours'}
        </button>
        {syncMutation.isSuccess && (
          <p className="text-sm text-green-600 mt-2">
            Synced {syncMutation.data?.synced} of {syncMutation.data?.total} stores successfully.
          </p>
        )}
        {syncMutation.isError && (
          <p className="text-sm text-red-600 mt-2">Failed to sync. Please try again.</p>
        )}
      </div>

      {/* Reset Setup */}
      <div className="mt-4 p-4 bg-gray-50 rounded-xl">
        <h3 className="text-sm font-medium text-gray-700 mb-2">Reset Setup</h3>
        <p className="text-sm text-gray-500 mb-3">
          Re-run the setup wizard to reconfigure your territory from scratch.
        </p>
        <button
          onClick={() => {
            localStorage.removeItem('setup_complete');
            window.location.href = '/setup';
          }}
          className="px-4 py-2 text-sm text-red-600 hover:text-red-700 font-medium"
        >
          Run Setup Wizard Again
        </button>
      </div>
    </div>
  );
}
